import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePrefetch } from "@/hooks/usePrefetch";
import logo from "@/assets/logo.png";

const navigation = [
  {
    name: "Leistungen",
    children: [
      { name: "Fulfillment", href: "/fulfillment" },
      { name: "Pre-FBA Service", href: "/pre-fba" },
      { name: "Seller Fulfilled Prime", href: "/seller-fulfilled-prime" },
      { name: "Lohnverpackung", href: "/lohnverpackung" },
      { name: "Aktionsversand", href: "/aktionsversand" },
      { name: "Retourenmanagement", href: "/retouren" },
      { name: "Fulfillment Dashboard", href: "/fulfillment-dashboard" },
    ],
  },
  {
    name: "Preise",
    href: "/preise",
  },
  { name: "FAQ", href: "/faq" },
  {
    name: "Über uns",
    children: [
      { name: "Über uns", href: "/ueber-uns" },
      { name: "greenmoose", href: "/greenmoose" },
      { name: "Qualitätsversprechen", href: "/qualitaetsversprechen" },
      { name: "Unser Fairplay", href: "/fairplay" },
      { name: "Karriere", href: "/karriere" },
      { name: "Blog", href: "/blog" },
      { name: "Podcast", href: "/podcast" },
    ],
  },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const prefetch = usePrefetch();

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
        <nav className="container-section flex items-center justify-between py-4 min-w-0">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="emoose fulfillment" width={581} height={126} className="h-14 sm:h-16 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.children ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                      {item.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    {openDropdown === item.name && (
                      <div className="absolute top-full left-0 pt-2">
                        <div className="bg-card rounded-xl shadow-card border border-border p-2 min-w-[200px]">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onMouseEnter={() => prefetch(child.href)}
                              className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                                isActive(child.href)
                                  ? "bg-accent text-accent-foreground font-medium"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              }`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href!}
                    onMouseEnter={() => prefetch(item.href!)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(item.href!)
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/anfrage" onMouseEnter={() => prefetch("/anfrage")}>
              <Button variant="default" size="lg">
                Anfrage
              </Button>
            </Link>
          </div>

          {/* Mobile: Menu Button */}
          <div className="lg:hidden flex items-center flex-shrink-0">
            <button
              className="p-2 text-foreground flex-shrink-0"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menü öffnen"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Navigation - outside header to avoid stacking context issues */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[72px] z-[60] bg-background overflow-y-auto">
          <div className="container-section py-4 space-y-2">
            {/* CTA Buttons prominent oben */}
            <div className="px-4 pb-3">
              <Link to="/anfrage" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="default" className="w-full" size="default">
                  Anfrage
                </Button>
              </Link>
            </div>

            <div className="border-t border-border pt-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.children ? (
                    <div className="space-y-1">
                      <span className="block px-4 py-2 text-sm font-semibold text-foreground">
                        {item.name}
                      </span>
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-8 py-2 text-sm text-muted-foreground hover:text-foreground"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      to={item.href!}
                      className="block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
